import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, withLatestFrom } from 'rxjs';
import { ApiStoreData } from '../core/store/utils';
import { validateAllFormFields } from 'src/app/core/utils/validators';
import { FormatCurrencyPipe } from '../core/utils/pipes';
import { FetchPatientBalance, MakePayment } from '../core/store/payments/payments.actions';
import { PaymentsState } from '../core/store/payments/payments.state';
import { PaymentMethodsState } from '../core/store/payment-methods/payment-methods.state';
import { PatientBalance, PaymentMethod, PaymentMethodType } from '../core/models';
import { Router } from '@angular/router';
import { Gtag } from 'angular-gtag';
import { asFormControl } from '../core/utils/common';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
})
export class PaymentsComponent implements OnInit {
  @Select(PaymentsState.makePaymentState) makePaymentState$!: Observable<ApiStoreData<null>>;
  @Select(PaymentsState.patientBalanceState) patientBalanceState$!: Observable<ApiStoreData<PatientBalance | null>>;
  @Select(PaymentMethodsState.defaultPaymentMethod) defaultPaymentMethod$!: Observable<PaymentMethod | null>;
  @Select(PaymentMethodsState.paymentMethodsState) paymentMethodsState$!: Observable<ApiStoreData<PaymentMethod[]>>;

  displayPaymentConfirmationModal: boolean = false;
  displayNotificationOverlay: boolean = false;
  displayNewPaymentMethodModal: boolean = false;
  makePaymentError?: string;
  currencyFormattingPipe = new FormatCurrencyPipe();

  makePaymentForm: FormGroup = new FormGroup({
    paymentAmount: new FormControl(null, [Validators.required, Validators.min(0.01)]),
    paymentMethodId: new FormControl(null, [Validators.required]),
  });

  selectedPaymentMethod: PaymentMethod | null = null;

  asFormControl = asFormControl;

  constructor(private store: Store, private router: Router, private gtag: Gtag) {}

  ngOnInit(): void {
    this.store.dispatch(new FetchPatientBalance());

    this.defaultPaymentMethod$.subscribe((defaultPaymentMethod: PaymentMethod | null) => {
      if (defaultPaymentMethod && !this.makePaymentForm.get('paymentMethodId')!.value) {
        this.makePaymentForm.patchValue({ paymentMethodId: defaultPaymentMethod.id });
        this.selectedPaymentMethod = defaultPaymentMethod;
      }
    });

    this.makePaymentForm.get('paymentMethodId')?.valueChanges.subscribe((paymentMethodId: string | null) => {
      this.selectedPaymentMethod = paymentMethodId
        ? this.store.selectSnapshot(PaymentMethodsState.paymentMethods).find((pm) => pm.id === paymentMethodId) || null
        : null;
    });
  }

  onSubmit() {
    if (!this.makePaymentForm.valid) {
      validateAllFormFields(this.makePaymentForm);
      return;
    }

    this.gtag.event('payments_submit_payment', { event_category: 'engagement' });
    this.showConfirmationModal();
  }

  showConfirmationModal() {
    this.displayPaymentConfirmationModal = true;
  }

  hideConfirmationModal() {
    this.displayPaymentConfirmationModal = false;
  }

  showNotificationOverlay() {
    this.displayNotificationOverlay = true;
  }

  hideNotificationOverlay() {
    this.displayNotificationOverlay = false;
  }

  showNewPaymentMethodModal() {
    this.displayNewPaymentMethodModal = true;
  }

  hideNewPaymentMethodModal() {
    this.displayNewPaymentMethodModal = false;
  }

  onConfirm() {
    this.store
      .dispatch(new MakePayment(this.makePaymentForm.value))
      .pipe(withLatestFrom(this.makePaymentState$))
      .subscribe(([_, makePaymentState]) => {
        if (makePaymentState.isFinished && makePaymentState.error) {
          this.makePaymentError = makePaymentState.error.uiMessage;
          this.hideConfirmationModal();
        } else {
          this.makePaymentError = undefined;
          this.hideConfirmationModal();
          this.showNotificationOverlay();
        }
      });
  }

  onCancel() {
    this.hideConfirmationModal();
  }

  getConfirmationMessage(): string {
    if (!this.selectedPaymentMethod) return '';

    const formattedPaymentAmount = this.currencyFormattingPipe.transform(this.makePaymentForm.value.paymentAmount);

    let type = '';
    let lastFourDigits = '';

    if (this.selectedPaymentMethod.type === PaymentMethodType.CreditCard) {
      type = this.selectedPaymentMethod.info.processor;
      const creditCardNumber = this.selectedPaymentMethod.info.number;
      lastFourDigits = creditCardNumber.substring(Math.max(creditCardNumber.length - 4, 0));
    } else if (this.selectedPaymentMethod.type === PaymentMethodType.BankAccount) {
      type = 'Bank Account';
      const bankAccountNumber = this.selectedPaymentMethod.info.accountNumber;
      lastFourDigits = bankAccountNumber.substring(Math.max(bankAccountNumber.length - 4, 0));
    }

    return `Please confirm your payment of ${formattedPaymentAmount} using ${type} ending in ${lastFourDigits}. Payment processing may take up to 24 hours.`;
  }

  onPaymentMethodCreated(newPaymentMethodId: string) {
    this.makePaymentForm.get('paymentMethodId')?.setValue(newPaymentMethodId);
  }

  onNotificationDismissed() {
    this.router.navigate(['/']);
  }
}
